import { Menu } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from '@vangst/lib/clsx'
import isEmptyOrNil from '@vangst/lib/isEmptyOrNil'
import { getKey as getConnectionKey } from '@vangst/services/oogst/company/useCompanyJobApplications'
import useDownloadMembers from '@vangst/services/oogst/jobPosting/useDownloadMembers'
import useGetMembersDownloads from '@vangst/services/oogst/jobPosting/useGetMembersDownloads'
import { MembersDownloadStatusEnum } from '@vangst/services/oogst/types'
import { memo, useEffect, useRef, useState } from 'react'
import { MdOutlineDescription } from 'react-icons/md'
import { DialogModal } from '../../../components/feedback/DialogModal'
import Attachment from '../../../components/forms/Attachment'
import useAttachmentz from '../../../components/forms/useAttachmentz'
import ClickyBtn from '../../../components/navigation/ClickyBtn'
import ClickyLink from '../../../components/navigation/ClickyLink'
import { MenuItemClicky, MenuItems } from '../../../components/navigation/Menus'

type PropsType = {
  readonly defaultValue?: string | null
}

function AttachmentMembers(props: PropsType) {
  const { defaultValue } = props
  const [downloadStatusId, setDownloadStatusId] = useState('')
  const [shouldPollUpload, setShouldPollUpload] = useState(true)
  const [inProgress, setInProgress] = useState(false)
  const [modalHeader, setModalHeader] = useState('')
  const [message, setMessage] = useState('')
  const focusRef = useRef<HTMLButtonElement>(null)

  const queryClient = useQueryClient()
  const hasValue = !isEmptyOrNil(defaultValue)
  const { attachment, handleSelected, remove } = useAttachmentz({
    attachment: hasValue
      ? { id: 'csvApplicants', url: defaultValue }
      : undefined,
    bucketType: 'csvApplicants',
  })
  const { blob, error, url, id } = attachment || {}

  const resetModal = () => {
    setMessage('')
    setModalHeader(``)
  }

  // used when we send the s3 url to the backend to begin processing.
  const { mutate: mutateDownloadMembers } = useDownloadMembers({
    onSuccess: (data) => {
      setDownloadStatusId(data.downloadMembers.id)
      // begins long-poling with useGetMembersDownloads
      setShouldPollUpload(true)
    },
    onError: (data) => {
      setModalHeader(`Error uploading the file to S3`)
      setMessage('ERROR: ' + data.message)
    },
  })

  // used when a file is selected for upload to S3
  const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setDownloadStatusId('')
    setInProgress(true)
    handleSelected(e)
  }

  // triggers when a file has been uploaded to S3, and the url is ready to send to the backend
  useEffect(() => {
    if (url != null && inProgress) {
      mutateDownloadMembers({
        input: {
          url: url,
        },
      })
      // resets attachment upload state
      if (id) remove(id)
    }
  }, [inProgress, mutateDownloadMembers, url, id, remove])

  // long poles for all MembersDownloads on the job, turned off/on by "shouldPollUpload" state.
  const { data } = useGetMembersDownloads(
    {},
    {
      refetchInterval: shouldPollUpload ? 1500 : false,
      enabled: shouldPollUpload,
      onSuccess: (data) => {
        const recentMembersDownload = data.getMembersDownloads.find(
          (x) => x.id === downloadStatusId,
        )
        if (
          recentMembersDownload?.status === MembersDownloadStatusEnum.Success
        ) {
          setShouldPollUpload(false)
          setInProgress(false)
          setModalHeader('CSV Successfully Processed')
          setMessage(
            'All new candidates have been processed. Please refresh the page.',
          )
          queryClient.invalidateQueries(getConnectionKey())
          queryClient.invalidateQueries([
            'CompanyJobApplications',
            { jobPostingId: id },
          ])
        } else if (
          recentMembersDownload?.status === MembersDownloadStatusEnum.Error
        ) {
          setShouldPollUpload(false)
          setInProgress(false)
          setModalHeader('CSV Upload Errors:')
          setMessage('ERRORS: ' + recentMembersDownload.failures)
        } else if (!recentMembersDownload && !downloadStatusId) {
          setShouldPollUpload(false)
          setInProgress(false)
        }
      },
      onError: () => {
        setModalHeader('Something Went Wrong :(')
        setMessage(
          'There was a problem checking on the status of csv processing, please refresh the page.',
        )
      },
    },
  )

  // on page load, sets the spinner if there is still a file being processed
  useEffect(() => {
    const jobInProgress = data?.getMembersDownloads.find(
      (x) => x.status === MembersDownloadStatusEnum.Started,
    )
    if (jobInProgress) {
      setDownloadStatusId(jobInProgress.id)
      setInProgress(true)
      setShouldPollUpload(true)
    }
  }, [setInProgress, setShouldPollUpload, setDownloadStatusId, data])

  // used when clicking on one of the MembersDownloads records
  const showModal = (membersDownload: {
    status: MembersDownloadStatusEnum
    failures?: string
  }) => {
    if (!membersDownload) return
    setModalHeader(`CSV Status: ${membersDownload.status}`)
    if (membersDownload.status === MembersDownloadStatusEnum.Error) {
      setMessage('ERRORS: ' + membersDownload.failures)
    } else if (membersDownload.status === MembersDownloadStatusEnum.Started) {
      setMessage('Candidate processing is in progress...')
    } else if (membersDownload.status === MembersDownloadStatusEnum.Pending) {
      setMessage('Candidate processing has been queued')
    } else {
      setMessage('Candidate Records Processed!')
    }
  }

  return (
    <>
      <div className="ml-auto hidden gap-4 text-sm sm:flex">
        {error != null && (
          <Attachment theme="failure">
            An error occurred uploading {blob?.name} to S3
          </Attachment>
        )}
        {inProgress && (
          // show the spinner
          <Attachment theme="pending">Processing candidates</Attachment>
        )}
        {!inProgress && (
          // otherwise show the upload button
          <label
            className="file-input flex items-center text-orange hocus:text-orange-medium"
            htmlFor="attachmentMembers"
          >
            Upload CSV
            <input
              accept=".csv"
              className="hidden"
              id="attachmentMembers"
              name="attachmentMembers"
              onChange={handleChange}
              type="file"
            />
          </label>
        )}
        <Menu as="div" className="relative">
          <Menu.Button as={ClickyLink} className="link-orange">
            Download Statuses
          </Menu.Button>
          <Menu.Items as={MenuItems}>
            {data?.getMembersDownloads.map((memberDownload) => {
              const isError =
                memberDownload.status === MembersDownloadStatusEnum.Error
              return (
                <MenuItemClicky
                  key={memberDownload.id}
                  onClick={() => showModal(memberDownload)}
                >
                  <span className={clsx(isError && 'text-red')}>
                    {memberDownload?.processingStartedAt || 'Recent Upload'}:{' '}
                    {memberDownload.status}
                  </span>
                </MenuItemClicky>
              )
            })}
          </Menu.Items>
        </Menu>
      </div>
      {message && (
        <DialogModal initialFocus={focusRef} onClose={resetModal} open>
          <DialogModal.Panel icon={MdOutlineDescription} title={modalHeader}>
            <DialogModal.Description className="my-8">
              {message}
            </DialogModal.Description>
            <div className="flex w-full items-center justify-center gap-4">
              <ClickyBtn
                className="mood-orange w-full"
                onClick={resetModal}
                ref={focusRef}
              >
                Close
              </ClickyBtn>
            </div>
          </DialogModal.Panel>
        </DialogModal>
      )}
    </>
  )
}

export default memo(AttachmentMembers)
